import React, {useEffect, useRef, useState} from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const labels = {breakfast: 'Frühstück', lunch: 'Mittagessen', dinner: 'Abendessen'};
const euro = value => new Intl.NumberFormat('de-DE', {style: 'currency', currency: 'EUR'}).format(value);
const components = [
  ['include_rolls', 'Brötchen', true], ['include_toppings', 'Beläge', true],
  ['include_boiled_eggs', 'Gekochte Eier', true], ['include_fried_eggs', 'Spiegeleier', true],
  ['include_scrambled_eggs', 'Rührei', true], ['include_coffee', 'Kaffee', false],
];

export default function MealSponsorModal({isOpen, onClose, employees, mealType, date,
  departmentId, onCompleted, initialSponsor = null}) {
  const [selectedId, setSelectedId] = useState('');
  const [preview, setPreview] = useState(null);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inFlight = useRef(false);
  const generation = useRef(0);
  const dialog = useRef(null);
  const passwordInput = useRef(null);

  useEffect(() => {
    generation.current += 1;
    inFlight.current = false;
    setSelectedId(initialSponsor?.id || '');
    setPreview(null); setPassword(''); setError(''); setResult(null); setBusy(false);
    if (!isOpen) return undefined;
    const previousFocus = document.activeElement;
    dialog.current?.querySelector('select, button')?.focus();
    return () => { generation.current += 1; previousFocus?.focus(); };
  }, [isOpen, departmentId, date, mealType, initialSponsor?.id]);

  useEffect(() => { if (preview && !result) passwordInput.current?.focus(); }, [preview, result]);

  if (!isOpen) return null;
  const selected = initialSponsor || employees.find(employee => employee.id === selectedId);
  const payload = {
    department_id: departmentId, date, meal_type: mealType,
    sponsor_employee_id: preview?.sponsor_id || selected?.id,
    sponsor_employee_name: preview?.sponsor || selected?.name,
  };

  const run = async execute => {
    if (inFlight.current) return;
    if (!selected) { setError('Bitte einen Zahler auswählen.'); return; }
    if (execute && !password) {
      setError('Bitte das Admin-Passwort der Wachabteilung eingeben.');
      passwordInput.current?.focus(); return;
    }
    inFlight.current = true; setBusy(true); setError('');
    const currentGeneration = generation.current;
    try {
      const response = await axios.post(`${API}/department-admin/sponsor-meal${execute ? '' : '/preview'}`,
        execute ? {...payload, admin_password: password, preview_token: preview.preview_token} : payload);
      if (currentGeneration !== generation.current) return;
      setPassword('');
      if (execute) {
        setResult(response.data);
        Promise.resolve(onCompleted?.()).catch(() => {
          if (currentGeneration === generation.current) setError('Buchung erfolgreich. Bitte die Übersicht neu laden.');
        });
      } else setPreview(response.data);
    } catch (failure) {
      if (currentGeneration !== generation.current) return;
      setPassword('');
      const detail = failure.response?.data?.detail;
      if (detail?.code === 'SPONSORING_PREVIEW_CHANGED') setPreview(detail.preview);
      setError(typeof detail === 'string' ? detail : detail?.message ||
        (execute ? 'Die Buchung konnte nicht bestätigt werden. Bitte erneut versuchen; derselbe Vorgang wird nicht doppelt gebucht.'
          : 'Die Vorschau konnte nicht geladen werden. Bitte erneut versuchen.'));
    } finally {
      if (currentGeneration === generation.current) { inFlight.current = false; setBusy(false); }
    }
  };

  const handleKeyDown = event => {
    if (event.key === 'Escape') { event.stopPropagation(); if (!busy) onClose(); }
    if (event.key !== 'Tab') return;
    const focusable = [...dialog.current.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled)')];
    const first = focusable[0], last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div ref={dialog} role="dialog" aria-modal="true" aria-labelledby="sponsor-title"
        aria-busy={busy} onKeyDown={handleKeyDown}
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
        <p className="mb-1 text-sm font-medium text-gray-500">{labels[mealType]} · {date?.split('-').reverse().join('.')}</p>
        <h2 id="sponsor-title" className="text-xl font-semibold text-gray-900">
          {result ? 'Sponsoring erfolgreich' : preview ? 'Sponsoring bestätigen' : 'Wer übernimmt die Kosten?'}
        </h2>
        {result ? (
          <div role="status" className="mt-5 rounded-lg bg-green-50 p-4 text-green-900">
            <p>{result.sponsor} übernimmt {euro(result.total_cost)} für {result.affected_employees} Mitarbeiter.</p>
          </div>
        ) : !preview ? (
          <div className="mt-5">
            <label htmlFor="sponsor-employee" className="mb-2 block text-sm font-medium">Zahler</label>
            {initialSponsor ? <p className="font-medium">{initialSponsor.name} · offenen Vorgang fortsetzen</p> : (
              <select id="sponsor-employee" value={selectedId} disabled={busy}
                onChange={event => setSelectedId(event.target.value)} className="w-full rounded-lg border border-gray-300 p-3">
                <option value="">Mitarbeiter auswählen …</option>
                {employees.map(employee => <option key={employee.id} value={employee.id}>{employee.name}</option>)}
              </select>
            )}
            <p className="mt-3 text-sm text-gray-600">Im nächsten Schritt siehst du die genaue Mitarbeiterzahl und die Gesamtkosten. Bis zur Bestätigung wird nichts gebucht.</p>
          </div>
        ) : (
          <form className="mt-5 space-y-4" noValidate onSubmit={event => {event.preventDefault(); run(true);}}>
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <p className="font-medium">Zahler: {preview.sponsor}</p>
              <p className="mt-2 text-lg">Sponsoring für <strong>{preview.affected_employees} Mitarbeiter</strong></p>
              <p className="mt-1 text-2xl font-bold">{euro(preview.total_cost)}</p>
              <p className="mt-2 text-sm text-gray-700">Dieser Betrag wird dem Konto des Zahlers belastet. Seine eigene Mahlzeit bleibt separat berechnet.</p>
            </div>
            <p className="text-sm text-gray-600">
              Enthalten: {mealType === 'breakfast' ? components.filter(([key, , defaultValue]) =>
                preview.config?.[key] ?? defaultValue).map(([, label]) => label).join(', ')
                : `${labels[mealType]} einschließlich Zusatzportionen`}.
              {preview.config?.excluded_categories?.length > 0 &&
                ` Ausgeschlossene Kategorien: ${preview.config.excluded_categories.map(category => category.name).join(', ')}.`}
              {preview.config?.exclude_guests && ' Gäste sind ausgeschlossen (Regel dieses bereits begonnenen Vorgangs).'}
              {preview.config?.exclude_external_guests && ' Externe Gäste anderer Wachen sind ausgeschlossen.'}
              {preview.config?.exclude_other_departments && ' Mitarbeiter anderer Wachabteilungen sind ausgeschlossen.'}
            </p>
            {preview.retroactive && <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
              <strong>Rückwirkendes Sponsoring:</strong> Bereits bezahlte Beträge werden den betroffenen Mitarbeiterkonten gutgeschrieben und dem Zahler nachträglich belastet.
            </p>}
            {preview.resuming && <p className="text-sm text-amber-900">Der offene Vorgang wird fortgesetzt. Bereits ausgeführte Buchungen werden nicht wiederholt.</p>}
            <div>
              <label htmlFor="sponsor-password" className="mb-2 block text-sm font-medium">Admin-Passwort dieser Wachabteilung</label>
              <input ref={passwordInput} id="sponsor-password" type="password" autoComplete="current-password"
                value={password} disabled={busy} onChange={event => setPassword(event.target.value)}
                className="w-full rounded-lg border border-gray-300 p-3" />
            </div>
            <button type="submit" disabled={busy} className="w-full rounded-lg bg-green-700 px-4 py-3 font-semibold text-white disabled:opacity-50">
              {busy ? 'Wird ausgeführt …' : 'Sponsoring verbindlich ausführen'}
            </button>
          </form>
        )}
        {error && <p role="alert" className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-800">{error}</p>}
        <div className="mt-5 flex gap-3">
          <button type="button" disabled={busy} onClick={onClose} className="rounded-lg border px-4 py-2 disabled:opacity-50">{result ? 'Schließen' : 'Abbrechen'}</button>
          {preview && !result && !initialSponsor && <button type="button" disabled={busy}
            onClick={() => {setPreview(null); setPassword(''); setError('');}}
            className="rounded-lg border px-4 py-2 disabled:opacity-50">Zurück</button>}
          {!preview && !result && <button type="button" disabled={busy || !selected} onClick={() => run(false)}
            className="ml-auto rounded-lg bg-blue-700 px-4 py-2 font-medium text-white disabled:opacity-50">{busy ? 'Vorschau wird geladen …' : 'Weiter zur Übersicht'}</button>}
        </div>
      </div>
    </div>
  );
}
